import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Pencil, Play, Save, Globe, GitCommitVertical, ChevronRight, Bot, ExternalLink, Link2, ChevronDown,
  Settings2,
} from 'lucide-react';
import { Button, Select } from '@dreamtree-org/twreact-ui';
import Can from '@rbac/Can';
import { useAppApi } from '@/lib/api';

/**
 * The builder's header: breadcrumb, inline rename, state chips, and the three verbs.
 *
 * "Publish" is the is_active toggle, not a draft/published split — there is one stored
 * definition and agents are offered it only while it is active. `version` is the counter
 * models/AppWorkflow.model.js bumps on every definition change; runs pin the version they
 * started under, so the chip is what tells you whether a run you are reading predates the
 * graph on screen.
 */

function Chip({ children, title, tone = 'muted' }) {
  const tones = {
    muted: 'border-[var(--app-border)] bg-[var(--app-surface)] text-secondary-500',
    warn: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    ok: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  };
  return (
    <span title={title} className={`px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap inline-flex items-center gap-1 ${tones[tone]}`}>
      {children}
    </span>
  );
}

/** Click-to-edit rather than a permanent input: the name is set once at creation and rarely
 *  touched, so a full-width text box would dominate a bar whose job is Save/Run/Publish. */
function InlineName({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <input
        autoFocus
        className="text-lg font-semibold bg-transparent border-b border-[var(--app-border)] outline-none min-w-0 flex-1"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(false); }}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Rename"
      className="group flex items-center gap-1.5 min-w-0"
    >
      <span className="text-lg font-semibold truncate">{value || 'Untitled workflow'}</span>
      <Pencil size={13} className="shrink-0 text-secondary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

function TopBarHeader({ form, dirty, published, onRename }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Link to="/app-workflow" className="text-sm font-medium text-secondary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
        Workflows
      </Link>
      <ChevronRight size={14} className="text-secondary-400 shrink-0" />
      <InlineName value={form.name} onChange={onRename} />
      {dirty && <Chip tone="warn" title="Unsaved changes">Editing</Chip>}
      <Chip title="Bumped on every saved definition change. Runs pin the version they started under.">
        <GitCommitVertical size={10} className="inline -mt-0.5 mr-0.5" />v{form.version || 1}
      </Chip>
      <Chip tone={published ? 'ok' : 'muted'} title={published ? 'Offered to agents' : 'Not offered to agents'}>
        {published ? 'Published' : 'Draft'}
      </Chip>
    </div>
  );
}

function SpecialistAgentLink({ agent }) {
  if (!agent) return null;
  return (
    <Link
      to={`/app-ai-agents/${agent.id}`}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${agent.name} (${agent.key}) in agent builder`}
      className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 bg-primary-50 dark:bg-primary-950/30 px-2 py-1.5 rounded border border-primary-500/20 whitespace-nowrap"
    >
      <Bot size={12} className="shrink-0" />
      <span className="max-w-[80px] truncate font-medium">{agent.name}</span>
      <ExternalLink size={10} className="shrink-0 text-primary-500" />
    </Link>
  );
}

function CallingAgentsPopover({ callingAgents }) {
  const [open, setOpen] = useState(false);
  if (!callingAgents.length) {
    return (
      <span
        title="No agents have this workflow attached as a tool"
        className="text-[11px] text-secondary-400 px-2 py-1.5 border border-dashed border-[var(--app-border)] rounded whitespace-nowrap hidden sm:inline"
      >
        0 callers
      </span>
    );
  }
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs px-2 py-1.5 rounded border border-[var(--app-border)] bg-[var(--app-surface)] text-secondary-600 hover:text-primary-600 shadow-xs whitespace-nowrap"
        title="Agents that can call this workflow as a tool"
      >
        <Link2 size={12} className="text-primary-600 shrink-0" />
        <span>{callingAgents.length} caller{callingAgents.length > 1 ? 's' : ''}</span>
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 right-0 z-30 w-56 p-2 rounded-md shadow-lg border border-[var(--app-border)] bg-[var(--app-surface)] space-y-1">
          <div className="text-[10px] font-semibold uppercase text-secondary-500 tracking-wider px-1">
            Calling Agents
          </div>
          {callingAgents.map((agent) => (
            <Link
              key={agent.id}
              to={`/app-ai-agents/${agent.id}`}
              className="flex items-center justify-between p-1.5 rounded hover:bg-secondary-500/10 text-xs text-[var(--app-text)]"
              onClick={() => setOpen(false)}
            >
              <div className="flex items-center gap-1.5 truncate">
                <Bot size={12} className="text-secondary-400 shrink-0" />
                <span className="truncate">{agent.name}</span>
              </div>
              <ExternalLink size={10} className="text-secondary-400 shrink-0 ml-1" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function resolveCallingAgents(agents, workflowId) {
  if (!workflowId || !Array.isArray(agents)) return [];
  return agents.filter((a) => {
    try {
      const ids = JSON.parse(a.workflow_ids || '[]');
      return Array.isArray(ids) && ids.map(Number).includes(Number(workflowId));
    } catch {
      return false;
    }
  });
}

/**
 * Agent and application scope are set once per workflow and then never touched, but they
 * were costing ~400px of a bar whose job is Run/Save/Publish. They live here instead; the
 * bound agent stays visible as SpecialistAgentLink, so nothing is hidden that you read at
 * a glance — only the controls you set once.
 */
function WorkflowSettingsPopover({ form, setField, agents, apps }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // The panel holds form controls over the canvas, so it must close on an outside click —
  // unlike the read-only callers popover, leaving it open would swallow canvas drags.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Agent and application scope for this workflow"
        className="flex items-center gap-1 text-xs px-2 py-1.5 rounded border shadow-xs whitespace-nowrap border-[var(--app-border)] bg-[var(--app-surface)] text-secondary-600 hover:text-primary-600"
      >
        <Settings2 size={12} className="shrink-0" />
        <span className="hidden sm:inline">Settings</span>
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full mt-1 right-0 z-30 w-72 p-3 rounded-md shadow-lg border border-[var(--app-border)] bg-[var(--app-surface)] space-y-3">
          <div className="space-y-1">
            <div className="text-[10px] font-semibold uppercase text-secondary-500 tracking-wider">Agent (optional)</div>
            <Select
              allowClear
              searchable
              placeholder="Select agent"
              options={agents.map((a) => ({ value: a.key, label: `${a.name} (${a.key})` }))}
              value={form.agent_key || null}
              onChange={(v) => setField('agent_key', v || '')}
            />
            <p className="text-[10px] text-secondary-500">
              Narrows the toolset and sets the run&apos;s default agent. Leave empty to use the
              company&apos;s full built-in toolset.
            </p>
          </div>

          {/* Only offered when there is something to scope to — with no applications the
              Select's one option ("Company-wide") is the only possible value. */}
          {apps.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase text-secondary-500 tracking-wider">Scope</div>
              <Select
                allowClear
                searchable
                placeholder="All applications"
                options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map((a) => ({ label: a.name, value: a.id }))]}
                value={form.application_id || ''}
                onChange={(v) => setField('application_id', v || null)}
              />
              <p className="text-[10px] text-secondary-500">
                Which application&apos;s callers may run this. Company-wide answers all of them.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TopBarActions({ dirty, saving, publishing, published, onRun, onSave, onTogglePublished }) {
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <Can module="workflows" action="run">
        <Button size="sm" variant="secondary" onClick={onRun} disabled={dirty} title={dirty ? 'Save first — a run executes the stored definition' : 'Run this workflow'}>
          <Play size={13} className="inline mr-1" /> Run
        </Button>
      </Can>

      <Can module="workflows" action="author">
        <Button size="sm" variant={dirty ? 'primary' : 'secondary'} onClick={onSave} disabled={saving || !dirty}>
          <Save size={13} className="inline mr-1" />{saving ? 'Saving…' : 'Save'}
        </Button>
        <Button
          size="sm"
          variant={published ? 'secondary' : 'primary'}
          onClick={onTogglePublished}
          disabled={publishing || dirty}
          title={dirty ? 'Save first' : 'An inactive workflow is not offered to agents'}
        >
          <Globe size={13} className="inline mr-1" />{published ? 'Unpublish' : 'Publish'}
        </Button>
      </Can>
    </div>
  );
}

export default function BuilderTopBar({
  form, setField, agents, dirty, saving, publishing, onSave, onRun, onTogglePublished,
}) {
  const { fetchApi } = useAppApi();
  const [apps, setApps] = useState([]);

  useEffect(() => {
    fetchApi('applicationsList').then((res) => setApps(Array.isArray(res) ? res : [])).catch(() => {});
  }, []);

  const published = !!form.is_active;
  const currentAgent = useMemo(
    () => (form.agent_key ? agents.find((a) => a.key === form.agent_key) : null),
    [agents, form.agent_key],
  );
  const callingAgents = useMemo(
    () => resolveCallingAgents(agents, form.id),
    [agents, form.id],
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
      <TopBarHeader
        form={form}
        dirty={dirty}
        published={published}
        onRename={(v) => setField('name', v)}
      />

      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <SpecialistAgentLink agent={currentAgent} />
        <CallingAgentsPopover callingAgents={callingAgents} />
        <WorkflowSettingsPopover
          form={form}
          setField={setField}
          agents={agents}
          apps={apps}
        />

        <TopBarActions
          dirty={dirty}
          saving={saving}
          publishing={publishing}
          published={published}
          onRun={onRun}
          onSave={onSave}
          onTogglePublished={onTogglePublished}
        />
      </div>
    </div>
  );
}


